import React from 'react';
import { Info, Calculator } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { NABadge } from './NABadge';

/**
 * METRIC_FORMULAS — central catalog of formulas displayed as tooltips on hover.
 * Keys map to KPI ids used across dashboards. Add new metrics here as they appear.
 */
export const METRIC_FORMULAS = {
  // ── Command Center / shared ──
  total_revenue:        { name: 'Total Revenue',        formula: 'Σ amount  WHERE is_return ≠ true   FROM {prefix}_transactions',          source: 'Live aggregation' },
  total_customers:      { name: 'Total Customers',      formula: 'COUNT(DISTINCT customer_id)  FROM {prefix}_customers',                    source: 'Live count' },
  total_transactions:   { name: 'Total Bills',          formula: 'COUNT(DISTINCT (store_code, bill_number, date))  FROM {prefix}_transactions', source: 'Live count' },
  total_stores:         { name: 'Total Stores',         formula: 'COUNT(DISTINCT store_code)  FROM {prefix}_stores  ∪ {prefix}_transactions', source: 'Live aggregation' },
  avg_atv:              { name: 'Avg Transaction Value',formula: 'total_revenue ÷ total_bills',                                              source: 'Derived' },
  repeat_ratio:         { name: 'Repeat Ratio',         formula: 'repeat_customers ÷ (one_timers + repeat_customers) × 100',                source: 'Derived' },
  one_timers:           { name: 'One-Time Customers',   formula: 'COUNT(customer_id)  WHERE transaction_count = 1',                         source: 'Live count' },
  repeat_customers:     { name: 'Repeat Customers',     formula: 'COUNT(customer_id)  WHERE transaction_count ≥ 2',                         source: 'Live count' },
  excl_same_day_repeat: { name: 'Repeat (Excl. Same Day)', formula: 'COUNT(DISTINCT customer)  WHERE bills on different dates ≥ 2',         source: 'Live aggregation' },
  visit_frequency:      { name: 'Visit Frequency',      formula: 'total_transactions ÷ total_customers',                                    source: 'Derived' },
  customer_ltv:         { name: 'Customer LTV',         formula: 'total_revenue ÷ total_customers',                                         source: 'Derived' },
  active_rate_90d:      { name: 'Active Rate (90d)',    formula: 'COUNT(customers with txn in last 90d) ÷ total_customers × 100',          source: 'Live count on customer.last_transaction_date' },

  // ── Points Economy ──
  total_points_earned:  { name: 'Total Points Earned',  formula: 'Σ points  WHERE bill_trans_type = "Earn"',                                source: 'Live aggregation' },
  total_points_redeemed:{ name: 'Total Points Redeemed',formula: 'Σ |points|  WHERE bill_trans_type = "Redeem"',                            source: 'Live aggregation' },
  earn_burn_ratio:      { name: 'Earn-Burn Ratio',      formula: 'total_points_redeemed ÷ total_points_earned × 100',                       source: 'Derived' },
  points_liability:     { name: 'Points Liability',     formula: '(earned − redeemed − expired) × point_value',                             source: 'Derived; point_value default = ₹1.0' },

  // ── RFM ──
  rfm_score:            { name: 'RFM Score',            formula: 'Quintile(R) + Quintile(F) + Quintile(M)  on real customer recency / frequency / monetary', source: '{prefix}_rfm_segments' },
  champions:            { name: 'Champions',            formula: 'Customers with R≥4 ∧ F≥4 ∧ M≥4',                                          source: '{prefix}_rfm_segments' },
  at_risk:              { name: 'At Risk',              formula: 'Customers with R≤2 ∧ F≥3',                                                source: '{prefix}_rfm_segments' },
  hibernating:          { name: 'Hibernating',          formula: 'Customers with R≤2 ∧ F≤2 ∧ M≥3',                                          source: '{prefix}_rfm_segments' },
  lost:                 { name: 'Lost',                 formula: 'Customers with R≤2 ∧ F≤2 ∧ M≤2',                                          source: '{prefix}_rfm_segments' },

  // ── Executive ──
  loyalty_contribution: { name: 'Loyalty Revenue Contribution', formula: 'loyalty_revenue ÷ total_business_revenue × 100',                  source: 'Requires non-loyalty transaction baseline (currently N/A)' },
  visit_lift:           { name: 'Visit Lift',           formula: '(member_frequency − non_member_frequency) ÷ non_member_frequency × 100', source: 'Requires non-member frequency baseline' },
  spend_lift:           { name: 'Spend Lift',           formula: '(member_atv − non_member_atv) ÷ non_member_atv × 100',                    source: 'Requires non-member ATV baseline' },
  net_margin:           { name: 'Net Loyalty Margin',   formula: '(loyalty_revenue − loyalty_cost) ÷ loyalty_revenue × 100',                source: 'Requires cost-of-goods data' },
  incremental_revenue:  { name: 'Incremental Revenue',  formula: 'loyalty_revenue − (total_customers × non_member_atv × non_member_freq)', source: 'Requires non-member baseline' },
  active_members:       { name: 'Active Members (90D)', formula: 'COUNT(customers with txn in last 90 days)',                               source: 'Live count on customer.last_transaction_date' },

  // ── Cohort & Migration ──
  cohort_migration_rate:{ name: 'Cohort Migration Rate',formula: '% of one-timers in month X who became repeat within N months',           source: 'Live cohort analysis on transaction history' },

  // ── Store ──
  store_revenue:        { name: 'Store Revenue',        formula: 'Σ amount  GROUP BY store_code',                                            source: 'Live aggregation' },
  store_health:         { name: 'Store Health Score',   formula: 'Deterministic by revenue rank (Top 3% = 95, Top 10% = 85, …)',            source: 'Computed' },

  // ── Campaigns ──
  delivery_rate:        { name: 'SMS Delivery Rate',    formula: 'Karix DLR delivered ÷ sent × 100',                                         source: 'Karix delivery report' },
  conversion_rate:      { name: 'Conversion Rate',      formula: 'COUNT(recipients with txn within 7d) ÷ delivered × 100',                  source: 'Joined query' },
  campaign_roi:         { name: 'Campaign ROI',         formula: '(revenue_attributed − sms_cost) ÷ sms_cost × 100',                        source: 'Derived' },

  // ── Points Economy (extra labels surfaced by points_economics endpoint) ──
  outstanding_points:   { name: 'Outstanding Points',   formula: 'Σ points_earned − Σ points_redeemed − Σ points_expired',                  source: '{prefix}_aggregated_stats.totals' },
  burn_rate:            { name: 'Burn Rate',            formula: 'total_points_redeemed ÷ total_points_earned × 100',                       source: 'Derived (alias of earn_burn_ratio)' },
  breakage_rate:        { name: 'Breakage Rate',        formula: 'expired_points ÷ total_earned × 100',                                     source: 'Live aggregation on point expiry' },
  active_redeemers:     { name: 'Active Redeemers',     formula: 'COUNT(DISTINCT customer_id)  WHERE bill_trans_type = "Redeem"  AND date ≥ 90d ago', source: 'Live aggregation' },
  expiry_risk:          { name: 'Expiry Risk',          formula: 'Σ points  WHERE expiry_date ≤ today + 90d',                                source: 'Live aggregation on points_ledger' },
  redemption_rate:      { name: 'Redemption Rate',      formula: 'redeemed_points ÷ earned_points × 100',                                    source: 'Derived' },
  avg_points_per_txn:   { name: 'Avg Points / Txn',     formula: 'total_points_earned ÷ total_earn_transactions',                            source: 'Derived' },

  // ── Customer / RFM ──
  customer_lifetime_value: { name: 'Customer LTV',      formula: 'total_revenue ÷ total_customers',                                          source: 'Derived (snapshot)' },
  avg_revenue_per_customer:{ name: 'Avg Revenue / Customer', formula: 'total_revenue ÷ total_customers',                                     source: 'Derived' },
  high_atv:             { name: 'High ATV',             formula: 'Top quartile customer ATV (Q4 of customer-level avg bill amount)',         source: '{prefix}_customers.aggregated_stats' },
  low_atv:              { name: 'Low ATV',              formula: 'Bottom quartile customer ATV',                                              source: '{prefix}_customers.aggregated_stats' },
  bottom_quartile:      { name: 'Bottom Quartile',      formula: 'P25 of customer-level total spend',                                        source: 'Live aggregation' },
  middle_50:            { name: 'Middle 50%',           formula: 'P25 → P75 of customer-level total spend',                                  source: 'Live aggregation' },
  top_quartile:         { name: 'Top Quartile',         formula: 'P75 → Max of customer-level total spend',                                  source: 'Live aggregation' },

  // ── Cohorts ──
  one_timer_visits:     { name: 'One-Timers',           formula: 'COUNT(customer_id)  WHERE transaction_count = 1',                          source: 'Live count' },
  repeat_2_5:           { name: 'Repeat (2-5)',         formula: 'COUNT(customer_id)  WHERE transaction_count BETWEEN 2 AND 5',              source: 'Live count' },
  repeat_6_10:          { name: 'Loyal (6-10)',         formula: 'COUNT(customer_id)  WHERE transaction_count BETWEEN 6 AND 10',             source: 'Live count' },
  repeat_11_20:         { name: 'Loyal (11-20)',        formula: 'COUNT(customer_id)  WHERE transaction_count BETWEEN 11 AND 20',            source: 'Live count' },
  champions_21:         { name: 'Champions (21+)',      formula: 'COUNT(customer_id)  WHERE transaction_count ≥ 21',                         source: 'Live count' },

  // ── Benchmarks ──
  avg_atv_benchmark:    { name: 'Avg ATV (Benchmark)',  formula: 'Industry benchmark ATV for the brand category',                            source: 'industry_benchmarks.json' },
};

/**
 * Resolve a free-form label (e.g. "One-Timers", "Burn Rate") to a metric key in
 * METRIC_FORMULAS so dashboards that surface dynamic labels can still get tooltips.
 */
export function resolveMetricKey(label) {
  if (!label) return null;
  const norm = String(label).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  // Direct hit
  if (METRIC_FORMULAS[norm]) return norm;
  // Common aliases
  const aliases = {
    one_timers: 'one_timers',
    one_timer: 'one_timers',
    repeat_customers: 'repeat_customers',
    repeat_ratio: 'repeat_ratio',
    total_customers: 'total_customers',
    total_revenue: 'total_revenue',
    total_transactions: 'total_transactions',
    total_bills: 'total_transactions',
    total_stores: 'total_stores',
    avg_atv: 'avg_atv',
    avg_transaction_value: 'avg_atv',
    visit_frequency: 'visit_frequency',
    customer_lifetime_value: 'customer_ltv',
    customer_ltv: 'customer_ltv',
    active_90d: 'active_rate_90d',
    active_members_90d: 'active_members',
    active_members_90_d: 'active_members',
    active_rate_90d: 'active_rate_90d',
    earn_burn_ratio: 'earn_burn_ratio',
    burn_rate: 'burn_rate',
    breakage_rate: 'breakage_rate',
    redemption_rate: 'redemption_rate',
    outstanding_points: 'outstanding_points',
    points_liability: 'points_liability',
    expiry_risk: 'expiry_risk',
    active_redeemers: 'active_redeemers',
    avg_points_txn: 'avg_points_per_txn',
    avg_points_transaction: 'avg_points_per_txn',
    repeat_2_5: 'repeat_2_5',
    repeat_6_10: 'repeat_6_10',
    repeat_11_20: 'repeat_11_20',
    champions_21: 'champions_21',
    high_atv: 'high_atv',
    low_atv: 'low_atv',
    bottom_quartile: 'bottom_quartile',
    middle_50: 'middle_50',
    top_quartile: 'top_quartile',
    avg_atv_benchmark: 'avg_atv_benchmark',
    avg_revenue_customer: 'avg_revenue_per_customer',
    incremental_revenue: 'incremental_revenue',
    visit_lift: 'visit_lift',
    spend_lift: 'spend_lift',
    loyalty_revenue_contribution: 'loyalty_contribution',
    net_loyalty_margin: 'net_margin',
    rfm_score: 'rfm_score',
    champions: 'champions',
    at_risk: 'at_risk',
    hibernating: 'hibernating',
    lost: 'lost',
  };
  return aliases[norm] || null;
}

/**
 * FormulaTooltip — wraps any element with a hover tooltip showing the formula.
 *
 * <FormulaTooltip metric="repeat_ratio">
 *   <span className="text-2xl font-bold">30.7%</span>
 * </FormulaTooltip>
 *
 * Or with explicit formula:
 *
 * <FormulaTooltip name="My Metric" formula="x / y * 100" source="Derived">
 *   ...
 * </FormulaTooltip>
 */
export const FormulaTooltip = ({
  metric,
  name,
  formula,
  source,
  children,
  showInfoIcon = true,
  iconClassName = '',
  side = 'top',
  dataTestId,
}) => {
  const meta = (metric && METRIC_FORMULAS[metric]) || { name: name || metric, formula: formula, source: source };
  if (!meta.formula && !meta.source) {
    // Nothing useful to show — render children as-is
    return children;
  }
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 cursor-help" data-testid={dataTestId || `formula-trigger-${metric || ''}`}>
            {children}
            {showInfoIcon && (
              <Info className={`w-3 h-3 text-muted-foreground/60 hover:text-primary transition ${iconClassName}`} />
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-md p-3 space-y-1.5">
          {meta.name && (
            <div className="font-semibold text-foreground inline-flex items-center gap-1.5">
              <Calculator className="w-3.5 h-3.5 text-violet-400" />
              {meta.name}
            </div>
          )}
          {meta.formula && (
            <div className="rounded bg-muted/50 px-2 py-1.5 font-mono text-[11px] leading-relaxed text-foreground/90 break-words whitespace-pre-wrap border border-border/30">
              {meta.formula}
            </div>
          )}
          {meta.source && (
            <div className="text-[10px] text-muted-foreground italic">
              Source: {meta.source}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * MetricLabel — drop-in replacement for label text that adds formula tooltip.
 *
 * <MetricLabel metric="repeat_ratio" />
 */
export const MetricLabel = ({ metric, label, className = '' }) => {
  const meta = METRIC_FORMULAS[metric];
  const text = label || meta?.name || metric;
  return (
    <FormulaTooltip metric={metric}>
      <span className={`${className}`}>{text}</span>
    </FormulaTooltip>
  );
};

/**
 * MetricCard — KPI card with built-in formula tooltip on label, N/A handling, and value.
 */
export const MetricCard = ({
  metric,
  label,
  value,
  formattedValue,
  na_reason,
  hint,
  className = '',
  valueClassName = 'text-2xl font-bold',
  dataTestId,
}) => {
  const isNA = value === null || value === undefined || (typeof value === 'number' && Number.isNaN(value));
  const meta = METRIC_FORMULAS[metric];
  const display = isNA ? null : (formattedValue !== undefined ? formattedValue : value);
  return (
    <div className={`p-3 rounded-lg bg-background/50 border border-border/50 ${className}`} data-testid={dataTestId}>
      <div className="flex items-start gap-1.5">
        <FormulaTooltip metric={metric} name={label || meta?.name}>
          <p className="text-xs text-muted-foreground">{label || meta?.name || metric}</p>
        </FormulaTooltip>
      </div>
      <div className={valueClassName}>
        {isNA ? <NABadge reason={na_reason} /> : display}
      </div>
      {hint && <p className="text-[10px] text-muted-foreground/70 mt-1">{hint}</p>}
    </div>
  );
};

export default FormulaTooltip;
